/**
 * GradeRecommendationCallout.jsx
 *
 * Renders the recommended carbide grade + design above the PLP grid on
 * the Tool Finder Results page (D9). Pure-data wrapper around
 * `bitSelectionService.selectGrade()` (Sub-Plan 0, Task 0.1).
 *
 * Returns null when:
 *   - The wizard hasn't captured enough rock-condition info to make a
 *     recommendation (selectGrade returns recommendation: null).
 *   - The selected productLine has no rules in bit-selection-guide.json
 *     (e.g. ITH / Raiseboring once those land in DRILLING_METHODS).
 *
 * MRT addition — no TF v4 equivalent. TF v4's bit-grade logic lives in
 * Apex (BitSelectionGuide__mdt); MRT ported it to pure JS as
 * `selectGrade()` so it can run client-side.
 *
 * Source notes:
 *   - Service: `src/services/bitSelectionService.js` (Sub-Plan 0).
 *   - Rule data: `src/data/bit-selection-guide.json` (Sub-Plan 0).
 *
 * @module components/wizard/GradeRecommendationCallout
 * @since 2026-05-18 (Group D Sub-Plan 3, Task SP3.B.2)
 */

import { useMemo } from 'react';
import { Lightbulb } from 'lucide-react';
import { useWizard } from '@contexts/WizardContext';
import { selectGrade } from '@services/bitSelectionService';

export default function GradeRecommendationCallout() {
  const { selections } = useWizard();

  const rec = useMemo(() => selectGrade({
    // bitSelectionService keys productLine on 'th' | 'dth' | 'rotary';
    // WizardContext stores it as uppercase 'TH' | 'DTH'. Normalise.
    productLine: selections.productLine?.toLowerCase() === 'dth' ? 'dth' : 'th',
    application: selections.application,
    rockConditions: {
      rockHardness: selections.rockCondition,
      // bitSelectionService also reads abrasivity, competence,
      // userIndicatedUnknown — none of those are captured in MRT's
      // current StepRockConditions (TF v4-only fields). Default them
      // here so selectGrade's predicate engine doesn't reject the rule
      // for missing keys; user can refine later via a richer step.
      abrasivity: null,
      competence: null,
      userIndicatedUnknown: !selections.rockCondition,
    },
  }), [selections.productLine, selections.application, selections.rockCondition]);

  if (!rec || !rec.recommendation) return null;

  const {
    recommendation,
    alternatives = [],
    confidence,
    rationale,
    sourceCitations = [],
  } = rec;

  return (
    <section
      role="region"
      aria-label="Recommended carbide grade"
      data-testid="grade-recommendation-callout"
      className="bg-eb-1/30 border-l-4 border-electric-blue p-4 mb-6"
    >
      <header className="flex items-start gap-3 mb-2">
        <Lightbulb
          size={20}
          className="text-electric-blue flex-shrink-0 mt-0.5"
          aria-hidden="true"
        />
        <div>
          <h3 className="font-sandvik-headline font-normal text-base text-carbon-black">
            Recommended carbide grade for your conditions
          </h3>
          {rationale && (
            <p className="font-sandvik-text text-sm text-n6 mt-0.5">{rationale}</p>
          )}
        </div>
      </header>

      <p className="font-sandvik-text text-sm text-carbon-black mb-2">
        <span className="font-normal">{recommendation.grade}</span>
        {recommendation.design && <> &middot; {recommendation.design}</>}
        {recommendation.buttonGeometry && <> &middot; {recommendation.buttonGeometry}</>}
        {confidence && (
          <span className="ml-2 px-2 py-0.5 bg-white text-xs tracking-wide text-n6 border border-n3">
            {confidence} confidence
          </span>
        )}
      </p>

      {alternatives.length > 0 && (
        <details className="font-sandvik-text text-sm text-n7">
          <summary className="cursor-pointer hover:text-carbon-black">
            Alternatives ({alternatives.length})
          </summary>
          <ul className="mt-2 ml-4 list-disc space-y-1">
            {alternatives.map((alt) => (
              <li key={alt.grade}>
                <span className="font-normal">{alt.grade}</span>
                {alt.design && <> ({alt.design})</>}
                {alt.rationale && <> &mdash; {alt.rationale}</>}
              </li>
            ))}
          </ul>
        </details>
      )}

      {sourceCitations.length > 0 && (
        <p className="font-sandvik-text text-xs text-n5 mt-2">
          Source: {sourceCitations.map((s) => `${s.id}${s.page ? ` p.${s.page}` : ''}`).join(', ')}
        </p>
      )}
    </section>
  );
}
